/**
 * Haptic feedback helpers.
 *
 * These give the PWA a light tactile "tap" on button presses and stronger cues
 * on important confirmations. Two transports:
 *   - Android Chrome / Chromium PWAs: the Vibration API (`navigator.vibrate`),
 *     with real per-pattern durations.
 *   - iOS Safari / installed PWAs (17.4+): no vibration API, but toggling a
 *     switch-style checkbox (`<input type="checkbox" switch>`) fires the system
 *     haptic tick — the same trick the ios-haptics package uses. Only tick
 *     count can vary there, not intensity.
 * Anywhere else every call is a graceful no-op (no error, no buzz) — see
 * `isHapticsSupported`.
 *
 * Gated behind a per-device localStorage preference (default ON) so users can
 * silence it. The toggle lives in profile → preferences (only shown on devices
 * that actually support haptics). Per-device on purpose: you may want it on
 * your phone and irrelevant on desktop, so it's not an account-wide setting.
 */

import { isIOS, isStandalone } from "./pwa";

const STORAGE_KEY = "hapticsEnabled";

export type HapticPattern =
  | "selection"
  | "light"
  | "medium"
  | "heavy"
  | "success"
  | "warning"
  | "error";

// Durations / patterns in ms. A number is a single buzz; an array alternates
// vibrate/pause. Kept deliberately short — subtle taps, not a phone ringer.
const PATTERNS: Record<HapticPattern, number | number[]> = {
  selection: 5,
  light: 10,
  medium: 18,
  heavy: 30,
  success: [10, 30, 14],
  warning: [16, 50, 16],
  error: [28, 40, 28, 40, 28],
};

function supportsVibrate(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.vibrate === "function";
}

// The switch-control haptic shipped in iOS/iPadOS 17.4, but Safari does not
// reliably expose a detectable `switch` property on checkbox inputs, so
// feature-detecting it hides haptics on iPhones that actually buzz. Gate by
// platform instead: on older iOS the hidden-switch click is a silent no-op.
function supportsIosSwitchHaptics(): boolean {
  return isIOS();
}

/* ------------- iOS switch-control transport ------------- */

// One scripted tick via the switch-control trick (the ios-haptics recipe).
// Verified 2026-09-07: current iOS ignores this entirely — only a genuine
// finger-toggle of a switch buzzes, which is what IosHapticOverlay provides.
// Kept for older iOS (17.4 up to the version that closed it), where it works.
function iosTick(): void {
  try {
    const label = document.createElement("label");
    label.ariaHidden = "true";
    label.style.display = "none";
    const input = document.createElement("input");
    input.type = "checkbox";
    input.setAttribute("switch", "");
    label.appendChild(input);
    document.head.appendChild(label);
    label.click();
    document.head.removeChild(label);
  } catch {
    // no haptics — fine
  }
}

/* ------------- diagnostics ------------- */

export interface HapticsDiagnostics {
  platform: "ios" | "android" | "other";
  iosVersion: string | null;
  standalone: boolean;
  hasVibrateApi: boolean;
  switchPropertyDetected: boolean;
  transport: "vibrate" | "ios-switch" | "none";
  enabled: boolean;
}

/** What this device actually offers — surfaced in the profile panel. */
export function hapticsDiagnostics(): HapticsDiagnostics {
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent;
  const m = ua.match(/OS (\d+)[._](\d+)(?:[._](\d+))?/);
  const ios = isIOS();
  return {
    platform: ios ? "ios" : /Android/i.test(ua) ? "android" : "other",
    iosVersion: ios && m ? [m[1], m[2], m[3]].filter(Boolean).join(".") : null,
    standalone: isStandalone(),
    hasVibrateApi: supportsVibrate(),
    switchPropertyDetected:
      typeof document !== "undefined" && "switch" in document.createElement("input"),
    transport: supportsVibrate() ? "vibrate" : ios ? "ios-switch" : "none",
    enabled,
  };
}

// The switch trick has one intensity, so patterns degrade to tick counts.
const IOS_TICKS: Record<HapticPattern, number> = {
  selection: 1,
  light: 1,
  medium: 1,
  heavy: 1,
  success: 2,
  warning: 2,
  error: 3,
};
const IOS_TICK_GAP_MS = 70;

/** True where a haptic transport exists (Vibration API, or the iOS switch trick). */
export function isHapticsSupported(): boolean {
  return supportsVibrate() || supportsIosSwitchHaptics();
}

function readEnabled(): boolean {
  if (typeof localStorage === "undefined") return true;
  return localStorage.getItem(STORAGE_KEY) !== "false"; // default on
}

// Cached so we don't hit localStorage on every button press; kept fresh by the
// setter below and by `storage` events from other tabs.
let enabled = readEnabled();

if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY) enabled = readEnabled();
  });
}

export function isHapticsEnabled(): boolean {
  return enabled;
}

/**
 * True where buttons must carry a real switch under the finger to buzz:
 * iOS with haptics on. Current iOS ignores scripted toggles (verified on a
 * real iPhone), but a genuine tap on an input[switch] still fires the
 * Taptic Engine, even when the switch is transparent and stretched over a
 * button. See `IosHapticOverlay`.
 */
export function needsIosTapOverlay(): boolean {
  return enabled && !supportsVibrate() && isIOS();
}

/** React-free subscription to the enabled flag (this tab + other tabs). */
export function subscribeHapticsEnabled(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) listener();
  };
  window.addEventListener("hapticsEnabledChanged", listener);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener("hapticsEnabledChanged", listener);
    window.removeEventListener("storage", onStorage);
  };
}

/** Persist the preference (per-device) and notify listeners in this tab. */
export function setHapticsEnabled(value: boolean): void {
  enabled = value;
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(STORAGE_KEY, value ? "true" : "false");
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("hapticsEnabledChanged"));
  }
}

/** Fire a haptic pulse. Safe no-op when unsupported or disabled by the user. */
export function haptic(pattern: HapticPattern = "light"): void {
  if (!enabled) return;
  if (supportsVibrate()) {
    try {
      navigator.vibrate(PATTERNS[pattern]);
    } catch {
      // Some browsers throw if vibrate is called outside a user gesture — ignore.
    }
    return;
  }
  if (supportsIosSwitchHaptics()) {
    // Extra ticks are scheduled tight so they stay inside the tap's
    // user-activation window; if iOS drops one, the first still lands.
    const ticks = IOS_TICKS[pattern] ?? 1;
    iosTick();
    for (let i = 1; i < ticks; i += 1) {
      setTimeout(iosTick, i * IOS_TICK_GAP_MS);
    }
  }
}

/**
 * Resolve the `haptic` prop on a Button into a concrete pattern (or null = off).
 * Centralises the "light tap by default" policy: flip the `undefined` case to
 * `null` here to make haptics opt-in instead of opt-out app-wide.
 */
export function resolveHaptic(value: boolean | HapticPattern | undefined): HapticPattern | null {
  if (value === false) return null;
  if (value === true || value === undefined) return "light";
  return value;
}
